import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Send, Lock, Smile, BellOff } from 'lucide-react';
import SafetyMenu from './SafetyMenu';
import ReportModal from './ReportModal';
import MentionAutocomplete from './MentionAutocomplete';

const REACTION_EMOJIS = ['😀', '😂', '❤️', '👍', '🙏', '🔥', '🎉', '😍', '😎', '👏'];

const Avatar = ({ src, name, size = 32 }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const EmojiPopover = ({ onPick, onClose, testid, anchorClassName = '' }) => (
  <div
    data-testid={testid}
    style={{ width: '240px' }}
    className={`absolute z-30 bottom-full mb-2 bg-white border border-gray-200 rounded-2xl shadow-lg p-2 ${anchorClassName}`}
    onClick={(e) => e.stopPropagation()}
  >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '4px',
      }}
    >
      {REACTION_EMOJIS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => {
            onPick(e);
            onClose && onClose();
          }}
          data-testid={`${testid}-${e}`}
          className="text-2xl h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center"
        >
          {e}
        </button>
      ))}
    </div>
  </div>
);

const ReactionsBar = ({ message, onToggle, mine }) => {
  const reactions = message.reactions || [];
  if (reactions.length === 0) return null;
  return (
    <div
      data-testid={`reactions-bar-${message.id}`}
      className={`flex flex-wrap gap-1 mt-1 ${mine ? 'justify-end' : 'justify-start'}`}
    >
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          onClick={() => onToggle(message.id, r.emoji, r.reacted_by_me)}
          data-testid={`reaction-${message.id}-${r.emoji}`}
          className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
            r.reacted_by_me
              ? 'bg-purple-50 border-purple-300 text-purple-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span className="text-sm leading-none">{r.emoji}</span>
          {r.count > 1 && <span className="text-[11px] font-semibold">{r.count}</span>}
        </button>
      ))}
    </div>
  );
};

const MessageBubble = ({ m, mine, onReact, onReport, t, isRTL }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
        <div className="relative group flex items-center gap-1">
          {/* React + Report triggers (only on others' messages) */}
          {!mine && (
            <>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                data-testid={`react-trigger-${m.id}`}
                className="opacity-60 hover:opacity-100 p-1 rounded-full text-gray-500"
                aria-label={t('react')}
              >
                <Smile className="w-4 h-4" />
              </button>
              <SafetyMenu
                testid={`msg-safety-${m.id}`}
                t={t}
                isRTL={isRTL}
                align="start"
                size="sm"
                items={[
                  {
                    key: 'report',
                    label: t('reportMessage'),
                    danger: true,
                    onClick: () => onReport && onReport(m.id),
                    testid: `msg-safety-report-${m.id}`,
                  },
                ]}
              />
            </>
          )}
          <div
            className={`relative px-3 py-2 rounded-2xl text-sm ${
              mine
                ? 'bg-purple-600 text-white rounded-br-sm'
                : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
            }`}
          >
            <p className="whitespace-pre-wrap break-words">{m.message}</p>
            <p
              className={`text-[10px] mt-1 ${
                mine ? 'text-purple-200' : 'text-gray-400'
              } text-end`}
            >
              {fmt(m.created_at)}
            </p>
          </div>
          {mine && (
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid={`react-trigger-${m.id}`}
              className="opacity-60 hover:opacity-100 p-1 rounded-full text-gray-500"
              aria-label={t('react')}
            >
              <Smile className="w-4 h-4" />
            </button>
          )}

          {pickerOpen && (
            <EmojiPopover
              testid={`react-picker-${m.id}`}
              anchorClassName={mine ? 'right-6' : 'left-6'}
              onPick={(e) => onReact(m.id, e, false)}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
        <ReactionsBar message={m} mine={mine} onToggle={onReact} />
      </div>
    </div>
  );
};

const PrivateChat = ({
  token,
  API,
  user,
  otherUserId,
  t,
  isRTL,
  onBack,
  onOpenProfile,
  onAfterRead,
  liveEvent,
}) => {
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [inputPickerOpen, setInputPickerOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { type, id }
  // Typing indicator — set when peer sends a `typing` event; auto-clears.
  const [peerTyping, setPeerTyping] = useState(false);
  const peerTypingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const scrollRef = useRef(null);
  const chatInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/private/${otherUserId}`, { headers });
      setThread(res.data);
      if (res.data?.status === 'active' || res.data?.conversation_id) {
        await axios.post(`${API}/chat/private/${otherUserId}/read`, {}, { headers });
        if (onAfterRead) onAfterRead();
      }
    } catch {
      setThread(null);
    }
    // eslint-disable-next-line
  }, [API, otherUserId, token]);

  useEffect(() => {
    load();
  }, [load]);

  // Phase 19 — append live messages from the open peer without
  // re-fetching the whole thread. Only act when the event matches the
  // current conversation's other user (sender_id === otherUserId).
  useEffect(() => {
    if (!liveEvent || liveEvent.type !== 'message_new') return;
    const m = liveEvent.data;
    if (!m?.id || m.sender_id !== otherUserId) return;
    setThread((prev) => {
      if (!prev) return prev;
      const messages = prev.messages || [];
      if (messages.some((x) => x.id === m.id)) return prev;
      return { ...prev, messages: [...messages, m] };
    });
    // A real message clears any stale typing indicator immediately.
    setPeerTyping(false);
    if (peerTypingTimerRef.current) {
      clearTimeout(peerTypingTimerRef.current);
      peerTypingTimerRef.current = null;
    }
    // Mark as read since we're actively viewing the thread.
    axios
      .post(`${API}/chat/private/${otherUserId}/read`, {}, { headers })
      .then(() => { if (onAfterRead) onAfterRead(); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [liveEvent, otherUserId, API, token]);

  // Phase 19+ — typing indicator. Show "X is typing…" when the open
  // peer publishes a `typing` event; auto-clear after 3 s of silence.
  useEffect(() => {
    if (!liveEvent || liveEvent.type !== 'typing') return;
    if (liveEvent.data?.from_user_id !== otherUserId) return;
    setPeerTyping(true);
    if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
    peerTypingTimerRef.current = setTimeout(() => {
      setPeerTyping(false);
      peerTypingTimerRef.current = null;
    }, 3000);
  }, [liveEvent, otherUserId]);

  // Cleanup the typing timer on unmount / chat switch.
  useEffect(() => {
    return () => {
      if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
    };
  }, []);

  // Throttled "I am typing" pinger — at most once every 2 s while
  // composing. The endpoint does no DB work and is best-effort, so we
  // swallow errors.
  const sendTypingPing = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    axios
      .post(`${API}/chat/private/${otherUserId}/typing`, {}, { headers })
      .catch(() => {});
    // eslint-disable-next-line
  }, [API, otherUserId, token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    if (text.length > 1000) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/private/${otherUserId}/send`, { message: text }, { headers });
      setInput('');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : t('error'));
    } finally {
      setSending(false);
    }
  };

  const onReact = async (messageId, emoji, isAlreadyMine) => {
    try {
      let res;
      if (isAlreadyMine) {
        res = await axios.delete(`${API}/chat/messages/${messageId}/react`, { headers });
      } else {
        res = await axios.post(
          `${API}/chat/messages/${messageId}/react`,
          { emoji },
          { headers }
        );
      }
      const reactions = res.data?.reactions || [];
      setThread((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).map((m) =>
            m.id === messageId ? { ...m, reactions } : m
          ),
        };
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) alert(typeof detail === 'string' ? detail : t('error'));
    }
  };

  const accept = async () => {
    if (!thread?.conversation_id) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/chat/requests/${thread.conversation_id}/accept`, {}, { headers });
      await load();
    } finally {
      setActionBusy(false);
    }
  };

  const decline = async () => {
    if (!thread?.conversation_id) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/chat/requests/${thread.conversation_id}/decline`, {}, { headers });
      await load();
    } finally {
      setActionBusy(false);
    }
  };

  const toggleMute = async () => {
    if (!thread?.conversation_id) return;
    setActionBusy(true);
    try {
      if (thread.muted) {
        await axios.delete(`${API}/chat/conversations/${thread.conversation_id}/mute`, { headers });
      } else {
        await axios.post(`${API}/chat/conversations/${thread.conversation_id}/mute`, {}, { headers });
      }
      setThread((p) => (p ? { ...p, muted: !p.muted } : p));
    } finally {
      setActionBusy(false);
    }
  };

  const hideConvo = async () => {
    if (!thread?.conversation_id) return;
    if (!window.confirm(t('hideConvoConfirm'))) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/chat/conversations/${thread.conversation_id}/hide`, {}, { headers });
      onBack && onBack();
    } finally {
      setActionBusy(false);
    }
  };

  const toggleBlock = async () => {
    const willBlock = !thread?.blocked_by_me;
    if (willBlock && !window.confirm(t('blockConfirm'))) return;
    if (!willBlock && !window.confirm(t('unblockConfirm'))) return;
    setActionBusy(true);
    try {
      if (willBlock) {
        await axios.post(`${API}/users/${otherUserId}/block`, {}, { headers });
      } else {
        await axios.delete(`${API}/users/${otherUserId}/block`, { headers });
      }
      await load();
    } catch {
      alert(t('error'));
    } finally {
      setActionBusy(false);
    }
  };

  if (!thread) {
    return (
      <div data-testid="chat-loading" className="min-h-screen flex items-center justify-center text-gray-500">
        {t('loading')}
      </div>
    );
  }

  const other = thread.other_user || {};
  const isPrivateRecipient = other.profile_privacy === 'private';
  const isRequester =
    thread.status === 'requested' && thread.permission_message === 'request_pending';
  const isRecipientOfRequest =
    thread.status === 'requested' && thread.permission_message === 'request_action_needed';
  const isDeclined = thread.status === 'declined';

  return (
    <div
      data-testid="chat-screen"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen flex flex-col bg-gray-50"
      onClick={() => setInputPickerOpen(false)}
    >
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-3 py-3 flex items-center gap-3">
          <button onClick={onBack} data-testid="chat-back" className="text-purple-600" aria-label={t('back')}>
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={() => onOpenProfile && onOpenProfile(other.id)}
            className="flex items-center gap-2 flex-1 min-w-0"
            data-testid="chat-header-user"
          >
            <Avatar src={other.profile_picture_url} name={other.name} size={36} />
            <div className="min-w-0 text-start">
              <p className="font-semibold text-gray-900 truncate">{other.name}</p>
              {other.nickname && <p className="text-xs text-gray-500 truncate">@{other.nickname}</p>}
            </div>
          </button>
          {isPrivateRecipient && (
            <span className="text-gray-400" title={t('privateProfile')}>
              <Lock className="w-4 h-4" />
            </span>
          )}
          {thread.muted && (
            <span data-testid="chat-muted-indicator" className="text-amber-500" title={t('muted')}>
              <BellOff className="w-4 h-4" />
            </span>
          )}
          <SafetyMenu
            testid="chat-safety-menu"
            t={t}
            isRTL={isRTL}
            align="end"
            size="md"
            items={[
              ...(thread.conversation_id
                ? [
                    {
                      key: 'mute',
                      label: thread.muted ? t('unmute') : t('mute'),
                      onClick: toggleMute,
                      testid: 'chat-safety-mute',
                    },
                    {
                      key: 'hide',
                      label: t('hideConvo'),
                      onClick: hideConvo,
                      testid: 'chat-safety-hide',
                    },
                  ]
                : []),
              {
                key: 'block',
                label: thread.blocked_by_me ? t('unblock') : t('block'),
                danger: !thread.blocked_by_me,
                onClick: toggleBlock,
                testid: 'chat-safety-block',
              },
              {
                key: 'report',
                label: t('reportUser'),
                danger: true,
                onClick: () => setReportTarget({ type: 'user', id: otherUserId }),
                testid: 'chat-safety-report',
              },
            ]}
          />
        </div>
      </header>

      <main ref={scrollRef} className="flex-1 overflow-y-auto max-w-2xl mx-auto w-full px-3 py-4 space-y-2">
        {(thread.messages || []).length === 0 && !isRecipientOfRequest && (
          <div className="text-center text-gray-400 text-sm py-10">
            {isPrivateRecipient && !thread.conversation_id ? t('privateMessageNotice') : '—'}
          </div>
        )}
        {(thread.messages || []).map((m) => (
          <MessageBubble
            key={m.id}
            m={m}
            mine={m.sender_id === user.id}
            onReact={onReact}
            onReport={(mid) => setReportTarget({ type: 'message', id: mid })}
            t={t}
            isRTL={isRTL}
          />
        ))}

        {thread.blocked_by_me && (
          <div data-testid="chat-blocked-by-me-banner" className="text-center text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mx-1 my-2">
            {t('youBlockedNotice')}
          </div>
        )}
        {thread.blocked_by_them && !thread.blocked_by_me && (
          <div data-testid="chat-blocked-by-them-banner" className="text-center text-xs text-gray-700 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 mx-1 my-2">
            {t('blockedByThemNotice')}
          </div>
        )}

        {isRequester && (
          <div className="text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mx-1 my-2">
            {t('requestSentHint')}
          </div>
        )}
        {isDeclined && (
          <div className="text-center text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mx-1 my-2">
            {t('requestDeclinedHint')}
          </div>
        )}
      </main>

      {isRecipientOfRequest ? (
        <footer className="sticky bottom-0 bg-white border-t border-gray-100">
          <div className="max-w-2xl mx-auto px-3 py-3">
            <p className="text-xs text-gray-600 mb-2">{t('requestPendingHint')}</p>
            <div className="flex gap-2">
              <button
                onClick={accept}
                disabled={actionBusy}
                data-testid="chat-accept-request"
                className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50"
              >
                {t('accept')}
              </button>
              <button
                onClick={decline}
                disabled={actionBusy}
                data-testid="chat-decline-request"
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                {t('decline')}
              </button>
            </div>
          </div>
        </footer>
      ) : thread.can_message ? (
        <footer className="sticky bottom-0 bg-white border-t border-gray-100">
          {peerTyping && thread.other_user && (
            <p
              data-testid="chat-typing-indicator"
              className="max-w-2xl mx-auto px-4 pt-1.5 pb-0 text-[12px] text-gray-500 italic"
            >
              {t('isTyping').replace('{name}', thread.other_user.name || '')}
            </p>
          )}
          {!thread.conversation_id && isPrivateRecipient && (
            <p className="max-w-2xl mx-auto px-3 pt-2 text-[11px] text-amber-700">
              {t('privateMessageNotice')}
            </p>
          )}
          <form
            onSubmit={send}
            className="max-w-2xl mx-auto px-3 py-3 flex gap-2 items-center relative"
            dir={isRTL ? 'rtl' : 'ltr'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => setInputPickerOpen((v) => !v)}
                data-testid="chat-emoji-toggle"
                aria-label={t('emoji')}
                className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                <Smile className="w-5 h-5" />
              </button>
              {inputPickerOpen && (
                <EmojiPopover
                  testid="chat-emoji-picker"
                  onPick={(e) => setInput((cur) => cur + e)}
                  onClose={() => setInputPickerOpen(false)}
                  anchorClassName={isRTL ? 'right-0' : 'left-0'}
                />
              )}
            </div>
            <div className="flex-1 relative">
              <MentionAutocomplete
                inputRef={chatInputRef}
                value={input}
                onChange={(next) => setInput(next)}
                token={token}
                API={API}
                isRTL={isRTL}
                testIdPrefix="chat-mention"
              />
              <input
                ref={chatInputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (e.target.value) sendTypingPing();
                }}
                maxLength={1000}
                placeholder={t('typeMessage')}
                data-testid="chat-input"
                className="w-full px-3 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:border-purple-400 bg-gray-50"
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="chat-send"
              className="px-4 py-2.5 rounded-full bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Send className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">{t('sendMessage')}</span>
            </button>
          </form>
        </footer>
      ) : (
        <footer className="sticky bottom-0 bg-white border-t border-gray-100">
          <div className="max-w-2xl mx-auto px-3 py-3 text-center text-sm text-gray-500">
            {isRequester ? t('requestSentHint') : isDeclined ? t('requestDeclinedHint') : '—'}
          </div>
        </footer>
      )}

      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        targetType={reportTarget?.type || 'user'}
        targetId={reportTarget?.id || ''}
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
      />
    </div>
  );
};

export default PrivateChat;
